import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Copy, Check, Languages } from 'lucide-react';

interface ExpandableCellProps {
  content: string | null;
  cost?: number;
  muted?: boolean;
  previewLength?: number;
  expandedPreviewLength?: number;
  forceExpand?: boolean;
}

const ExpandableCell = ({ content, cost, muted = false, previewLength = 100, expandedPreviewLength = 250, forceExpand }: ExpandableCellProps) => {
  const [isFullyExpanded, setIsFullyExpanded] = useState(false);
  const [isPartiallyExpanded, setIsPartiallyExpanded] = useState(false);

  useEffect(() => {
    if (forceExpand) {
      setIsPartiallyExpanded(true);
      setIsFullyExpanded(true);
    } else if (forceExpand === false) {
      setIsPartiallyExpanded(false);
      setIsFullyExpanded(false);
    }
  }, [forceExpand]);
  const [copied, setCopied] = useState(false);
  
  // Translation states
  const [translatedContent, setTranslatedContent] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslated, setShowTranslated] = useState(false);
  
  // Handle the case when content is null
  const displayContent = content || '';
  
  // Check if content needs expansion
  const shouldShowExpandButton = displayContent.length > previewLength;
  
  // Get preview content based on expansion state
  let previewContent = displayContent;
  
  if (isFullyExpanded) {
    previewContent = showTranslated && translatedContent ? translatedContent : displayContent;
  } else if (isPartiallyExpanded) {
    previewContent = (showTranslated && translatedContent ? translatedContent : displayContent).slice(0, expandedPreviewLength) + '...';
  } else {
    previewContent = (showTranslated && translatedContent ? translatedContent : displayContent).slice(0, previewLength) + (shouldShowExpandButton ? '...' : '');
  }
  
  const handleCopy = async () => {
    try {
      const textToCopy = showTranslated && translatedContent ? translatedContent : displayContent;
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };
  
  const handleTranslate = async () => {
    if (isTranslating) return;
    
    setIsTranslating(true);
    try {
      const response = await fetch('https://openai-assistants-cloudfare-worker-boilerplate.chris-172.workers.dev/claude', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          input: displayContent,
          instructionText: "Translate the following text to English. If the text is already in English, translate it to the language best suited for the user based on context. Provide only the translation without any explanations.",
          savedMessages: [{ role: 'user', content: displayContent }]
        }),
      });

      if (!response.ok) {
        throw new Error('Translation failed');
      }

      const translatedText = await response.text();
      setTranslatedContent(translatedText);
      setShowTranslated(true);
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setIsTranslating(false);
    }
  };
  
  return (
    <div className="relative group">
      <div className="flex items-center gap-2">
        <div className={`${muted ? 'text-gray-400' : 'text-white'}`}>
          {previewContent}
        </div>
        <div className="text-xs text-gray-500">
          ({displayContent.length})
        </div>
        {cost !== undefined && cost !== null && (
          <div className="text-xs text-blue-400">
            ${typeof cost === 'string' ? parseFloat(cost).toFixed(4) : cost.toFixed(4)}
          </div>
        )}
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          title={copied ? "Copied!" : "Copy to clipboard"}
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <Copy className="w-4 h-4 text-gray-400 hover:text-gray-300" />
          )}
        </button>
        <button
          onClick={() => {
            if (translatedContent && showTranslated) {
              setShowTranslated(false);
            } else if (translatedContent) {
              setShowTranslated(true);
            } else {
              handleTranslate();
            }
          }}
          className={`opacity-0 group-hover:opacity-100 transition-opacity ${isTranslating ? 'animate-pulse' : ''}`}
          disabled={isTranslating}
        >
          <Languages className={`w-4 h-4 ${showTranslated ? 'text-blue-400' : 'text-gray-400'} hover:text-gray-300`} />
        </button>
        {shouldShowExpandButton && (
          <button
            onClick={() => {
              if (!isPartiallyExpanded) {
                setIsPartiallyExpanded(true);
              } else {
                setIsPartiallyExpanded(false);
                setIsFullyExpanded(false);
              }
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {isPartiallyExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </button>
        )}
      </div>
      {showTranslated && translatedContent && (
        <div className="flex gap-2 mt-1 text-xs">
          <button
            onClick={() => setShowTranslated(false)}
            className={`px-2 py-1 rounded ${!showTranslated ? 'bg-gray-600 text-white' : 'text-gray-400'}`}
          >
            Original
          </button>
          <button
            onClick={() => setShowTranslated(true)}
            className={`px-2 py-1 rounded ${showTranslated ? 'bg-gray-600 text-white' : 'text-gray-400'}`}
          >
            Translated
          </button>
        </div>
      )}
      {isPartiallyExpanded && !isFullyExpanded && displayContent.length > expandedPreviewLength && (
        <button
          onClick={() => setIsFullyExpanded(true)}
          className="text-blue-400 text-sm hover:text-blue-300 mt-1 underline"
        >
          Read more
        </button>
      )}
    </div>
  );
};

export default ExpandableCell;